// =============================================================================
// Qualification Application  —  route: /apply/application
// Multi-step form with progress indicator, validation, scoring, and submission.
// On success it scores the application and routes to /apply/result.
// =============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, TextAreaField, RadioCards, ProgressBar } from './components/FormControls';
import {
  COHORT,
  GOAL_OPTIONS,
  YES_NO_OPTIONS,
  LEADS_OPTIONS,
  HOURS_OPTIONS,
  INCOME_OPTIONS,
  QUESTION_LABELS,
} from './funnelConfig';
import { scoreApplication } from './scoring';
import { submitLead, isDuplicateSubmission } from './leadStore';
import { captureAttribution, getUtmParams, getAttribution, trackEvent } from './tracking';

// Step order: one low-friction question to open, then contact details while
// intent is still high. Contact used to sit LAST — the applicant spent 100% of
// the effort before leaving anything followable, so every abandon was
// unreachable. Capturing name/email at step 2 means a drop-off after this point
// is still a person we can email.
//
// CONTACT_STEP must stay in sync with the step that renders the contact fields:
// it is the step that fires the Meta `Lead` standard event.
const STEP_LABELS = ['Your Goal', 'About You', 'Background', 'Growth', 'Commitment', 'Motivation'];
const TOTAL_STEPS = STEP_LABELS.length;
const CONTACT_STEP = 2;

const INITIAL_ANSWERS = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  location: '',
  goalType: '',
  salesExperience: '',
  businessExperience: '',
  comfortableLeads: '',
  hoursPerWeek: '',
  incomeGoal: '',
  understandsCommission: '',
  whyInterested: '',
  successReason: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Validate the fields shown on a given step. Returns an errors map. */
function validateStep(step, a) {
  const e = {};
  const required = (key) => {
    if (!a[key]) e[key] = 'Please select an option';
  };

  if (step === 1) required('goalType');
  if (step === 2) {
    if (!a.firstName.trim()) e.firstName = 'Required';
    if (!a.lastName.trim()) e.lastName = 'Required';
    if (!a.email.trim()) e.email = 'Required';
    else if (!EMAIL_RE.test(a.email.trim())) e.email = 'Enter a valid email address';
    if (!a.phone.trim()) e.phone = 'Required';
    else if (a.phone.replace(/\D/g, '').length < 10) e.phone = 'Enter a valid phone number';
    if (!a.location.trim()) e.location = 'Required';
  }
  if (step === 3) {
    required('salesExperience');
    required('businessExperience');
  }
  if (step === 4) {
    required('comfortableLeads');
    required('hoursPerWeek');
  }
  if (step === 5) {
    required('incomeGoal');
    required('understandsCommission');
  }
  // Step 6 (the written answers) is intentionally OPTIONAL. It previously
  // demanded 8+ words in each of two essay boxes, which rejected complete
  // sentences and killed the form on mobile. Depth is now gathered at the
  // qualified stage, where we already ask for a resume and a client plan.
  return e;
}

function ApplicationForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Meta's `Lead` must be counted once per application. Without this guard,
  // pressing Back to step 2 and Continuing again re-fires it and inflates the
  // conversion count the campaign is being optimised against.
  const contactCapturedRef = useRef(false);

  useEffect(() => {
    captureAttribution();
    trackEvent('application_started');
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    trackEvent('application_step_viewed', {
      step,
      step_label: STEP_LABELS[step - 1],
    });
  }, [step]);

  const handleChange = (name, value) => {
    setAnswers((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    if (submitError) setSubmitError('');
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const handleNext = () => {
    const stepErrors = validateStep(step, answers);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      trackEvent('application_step_invalid', {
        step,
        step_label: STEP_LABELS[step - 1],
        field_errors: Object.keys(stepErrors),
      });
      return;
    }
    setErrors({});
    trackEvent('application_step_completed', {
      step,
      step_label: STEP_LABELS[step - 1],
    });
    // Contact details are now in hand — this is the real "lead" moment, and the
    // event Meta optimises against. Guarded to fire once per application.
    if (step === CONTACT_STEP && !contactCapturedRef.current) {
      contactCapturedRef.current = true;
      trackEvent('application_contact_captured', {
        step,
        step_label: STEP_LABELS[step - 1],
      });
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const handleSubmit = async () => {
    if (submitting) return; // guard against double-clicks

    // The last step is optional now, so validating it alone would wave through
    // an application missing required answers from an earlier step. Re-check
    // every step and send the user back to the first one that fails.
    for (let s = 1; s <= TOTAL_STEPS; s += 1) {
      const stepErrors = validateStep(s, answers);
      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setStep(s);
        return;
      }
    }

    const email = answers.email.trim().toLowerCase();
    if (isDuplicateSubmission(email)) {
      setSubmitError(
        'It looks like you just submitted this application. Please wait a moment before trying again.',
      );
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      const { score, tier, breakdown, flags } = scoreApplication(answers);
      const cleaned = {
        ...answers,
        firstName: answers.firstName.trim(),
        lastName: answers.lastName.trim(),
        email,
        phone: answers.phone.trim(),
        location: answers.location.trim(),
        whyInterested: answers.whyInterested.trim(),
        successReason: answers.successReason.trim(),
      };

      const lead = await submitLead({
        ...cleaned,
        score,
        tier,
        scoreBreakdown: breakdown,
        flags,
        utm: getUtmParams(),
        attribution: getAttribution(),
      });

      trackEvent('application_completed', { tier, score, flags });
      // Navigate to the result page; pass the scored result via router state.
      navigate('/result', { state: { result: { ...lead, score, tier, breakdown, flags } } });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ApplicationForm] submission failed', err);
      trackEvent('application_submit_failed', { message: String(err && err.message) || 'unknown' });
      setSubmitError('Something went wrong submitting your application. Please try again.');
      setSubmitting(false);
    }
  };

  const isLastStep = step === TOTAL_STEPS;

  const stepContent = useMemo(() => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-5">
            <StepHeading
              title="What are you actually looking for?"
              subtitle="Be honest — this helps us point you in the right direction."
            />
            <RadioCards
              label="Are you looking for a job, side hustle, or business opportunity?"
              name="goalType"
              value={answers.goalType}
              onChange={handleChange}
              options={GOAL_OPTIONS}
              error={errors.goalType}
            />
          </div>
        );
      case 2:
        return (
          <div className="space-y-5">
            <StepHeading
              title="How do we reach you?"
              subtitle="So we can follow up about your application and the next cohort."
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField
                label="First name"
                name="firstName"
                value={answers.firstName}
                onChange={handleChange}
                error={errors.firstName}
                autoComplete="given-name"
              />
              <TextField
                label="Last name"
                name="lastName"
                value={answers.lastName}
                onChange={handleChange}
                error={errors.lastName}
                autoComplete="family-name"
              />
            </div>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={answers.email}
              onChange={handleChange}
              error={errors.email}
              autoComplete="email"
              placeholder="you@example.com"
            />
            <TextField
              label="Phone"
              name="phone"
              type="tel"
              value={answers.phone}
              onChange={handleChange}
              error={errors.phone}
              autoComplete="tel"
              placeholder="(555) 555-5555"
            />
            <TextField
              label="City / Province"
              name="location"
              value={answers.location}
              onChange={handleChange}
              error={errors.location}
              placeholder="e.g. Toronto, ON"
            />
          </div>
        );
      case 3:
        return (
          <div className="space-y-7">
            <StepHeading
              title="Your background"
              subtitle="Entrepreneurial and sales experience tells us a lot about fit."
            />
            <RadioCards
              label={QUESTION_LABELS.salesExperience}
              name="salesExperience"
              value={answers.salesExperience}
              onChange={handleChange}
              options={YES_NO_OPTIONS}
              error={errors.salesExperience}
            />
            <RadioCards
              label={QUESTION_LABELS.businessExperience}
              name="businessExperience"
              value={answers.businessExperience}
              onChange={handleChange}
              options={YES_NO_OPTIONS}
              error={errors.businessExperience}
            />
          </div>
        );
      case 4:
        return (
          <div className="space-y-7">
            <StepHeading
              title="How you'll grow the business"
              subtitle="A travel business is built on relationships and consistent effort."
            />
            <RadioCards
              label={QUESTION_LABELS.comfortableLeads}
              name="comfortableLeads"
              value={answers.comfortableLeads}
              onChange={handleChange}
              options={LEADS_OPTIONS}
              error={errors.comfortableLeads}
            />
            <RadioCards
              label={QUESTION_LABELS.hoursPerWeek}
              name="hoursPerWeek"
              value={answers.hoursPerWeek}
              onChange={handleChange}
              options={HOURS_OPTIONS}
              error={errors.hoursPerWeek}
            />
          </div>
        );
      case 5:
        return (
          <div className="space-y-7">
            <StepHeading
              title="Your commitment"
              subtitle="This is a commission-based business opportunity, not a salaried job."
            />
            <RadioCards
              label={QUESTION_LABELS.incomeGoal}
              name="incomeGoal"
              value={answers.incomeGoal}
              onChange={handleChange}
              options={INCOME_OPTIONS}
              error={errors.incomeGoal}
            />
            <RadioCards
              label={QUESTION_LABELS.understandsCommission}
              name="understandsCommission"
              value={answers.understandsCommission}
              onChange={handleChange}
              options={YES_NO_OPTIONS}
              helpText="You earn commission on the travel you sell. There is no base salary."
              error={errors.understandsCommission}
            />
          </div>
        );
      case 6:
      default:
        return (
          <div className="space-y-6">
            <StepHeading
              title="Anything you'd like to add?"
              subtitle="Optional — skip straight to submit if you'd rather. Strong answers help, but they will never hold your application back."
            />
            <TextAreaField
              label={`${QUESTION_LABELS.whyInterested} (optional)`}
              name="whyInterested"
              value={answers.whyInterested}
              onChange={handleChange}
              error={errors.whyInterested}
              rows={4}
              placeholder="What draws you to building a travel business?"
            />
            <TextAreaField
              label={`${QUESTION_LABELS.successReason} (optional)`}
              name="successReason"
              value={answers.successReason}
              onChange={handleChange}
              error={errors.successReason}
              rows={4}
              placeholder="What makes you confident you'd succeed in a commission-based role?"
            />
          </div>
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, answers, errors]);

  return (
    <div className="landing-page min-h-screen bg-revolution-gray">
      {/* Deliberately NOT the site <Header />. That nav puts nine outbound links
          — including "Book Now" — on the one page whose only job is to finish
          the application. This bar is branding with no exits. */}
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex max-w-2xl items-center justify-center px-4 py-4 sm:px-6">
          <img
            src="https://s3.amazonaws.com/ttand/uploads/users/logos/smills.jpg"
            alt="Revolution Travel Group"
            className="h-14 w-auto object-contain"
          />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-extrabold text-revolution-navy sm:text-3xl">
            Entrepreneur Application
          </h1>
          <p className="mt-2 text-gray-600">
            A selective application. Your answers help us assess whether this
            commission-based business opportunity is a strong fit.
          </p>
          <p className="mt-3 text-sm font-semibold text-revolution-orange">
            {COHORT.deadline}
          </p>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-lg sm:p-8">
          <ProgressBar current={step} total={TOTAL_STEPS} label={STEP_LABELS[step - 1]} />

          <div className="mt-7">{stepContent}</div>

          {submitError && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {submitError}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-4">
            {step > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                disabled={submitting}
                className="rounded-lg px-5 py-3 font-semibold text-gray-600 transition hover:bg-gray-100 disabled:opacity-50"
              >
                Back
              </button>
            ) : (
              <span />
            )}

            {isLastStep ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center justify-center rounded-lg bg-revolution-orange px-7 py-3 font-bold text-white shadow transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Submitting…' : 'Submit Application'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center justify-center rounded-lg bg-revolution-blue px-7 py-3 font-bold text-white shadow transition hover:bg-blue-700"
              >
                Continue
              </button>
            )}
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-gray-500">
          Your information is used only to assess fit and follow up about this opportunity.
        </p>
      </main>

      {/* Minimal footer — no outbound links on the application page. */}
      <footer className="pb-10 text-center text-xs text-gray-500">
        &copy; {new Date().getFullYear()} Revolution Travel Group
      </footer>
    </div>
  );
}

function StepHeading({ title, subtitle }) {
  return (
    <div>
      <h2 className="text-xl font-bold text-revolution-navy">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
  );
}

export default ApplicationForm;
